import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  FolderArchive,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import type { PoleResult, TechCalcDrawing, VariantResults } from "../types";
import {
  calcSteps,
  compareRefs,
  drawingPoints,
  fmtBundle,
  fmtDateTime,
  fmtForce,
  fmtMeters,
  poleDisplayLabel,
  spansOf,
} from "../utils";

function StatusBadge({ r }: { r: PoleResult | undefined }) {
  if (!r || !r.ok) {
    return (
      <span
        className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-[var(--color-text-muted)]"
        title={r?.error || "Słup nie został policzony"}
      >
        Błąd
      </span>
    );
  }
  // Werdykt to spełnienie warunku wytrzymałości słupa (obie osie), nie ocena słupa.
  return r.passed ? (
    <span className="border border-emerald-600 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-emerald-600">
      Spełniony
    </span>
  ) : (
    <span className="border border-red-600 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-red-600">
      Niespełniony
    </span>
  );
}

function ForcePair({
  label,
  value,
  allowed,
  passed,
}: {
  label: string;
  value: number | null;
  allowed: number | null;
  passed: boolean;
}) {
  return (
    <span className="font-mono text-[11px]">
      <span className="text-[var(--color-text-tertiary)]">{label} </span>
      <span className={passed ? "text-emerald-600" : "text-red-600"}>{fmtForce(value)}</span>
      <span className="text-[var(--color-text-muted)]"> / {fmtForce(allowed)}</span>
    </span>
  );
}

/**
 * Lista słupów z wynikami obliczeń: siły obliczeniowe vs dopuszczalne w obu
 * kierunkach, status, rozwijane wzory (linie mono z backendu) i wejście do
 * karty obliczeń pojedynczego słupa.
 */
export function TechCalcResultsPanel({
  projectId,
  variantId,
  drawing,
  data,
  loading,
  onRecompute,
  recomputePending,
  onExport,
  exportPending,
  onDownloadCards,
  downloadCardsPending,
  dirty,
}: {
  projectId: string;
  variantId: string;
  drawing: TechCalcDrawing | null | undefined;
  /** undefined = still loading or never computed (results/ may 404). */
  data: VariantResults | undefined;
  loading: boolean;
  onRecompute: () => void;
  recomputePending: boolean;
  onExport: () => void;
  exportPending: boolean;
  /** ZIP kart obliczeń (PDF) wszystkich policzonych słupów. */
  onDownloadCards: () => void;
  downloadCardsPending: boolean;
  /** Unsaved drawing changes — recompute would run on the OLD saved drawing. */
  dirty: boolean;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [outOfScopeOpen, setOutOfScopeOpen] = useState(false);

  const allPoles = drawingPoints(drawing)
    .filter((p) => p.properties.kind === "pole")
    .sort((a, b) => compareRefs(a.properties.ref, b.properties.ref));
  // Słupy „poza opracowaniem" nie mają wyników — osobna zwinięta sekcja na dole,
  // żeby lista wyników pokazywała wyłącznie to, co faktycznie policzono.
  const poles = allPoles.filter((p) => !p.properties.params.out_of_scope);
  const outOfScope = allPoles.filter((p) => !!p.properties.params.out_of_scope);
  const resultMap = data?.results?.poles ?? null;
  const hasResults = !!resultMap && !!data?.computed_at;

  const pointsByRef = new Map(
    drawingPoints(drawing).map((p) => [p.properties.ref, p.properties]),
  );
  /** Etykieta drugiego końca przęsła: priorytet nazw; przyłącze jako „przyłącze". */
  const otherEndLabel = (poleRef: string, from: string, to: string): string => {
    const otherRef = from === poleRef ? to : from;
    const other = pointsByRef.get(otherRef);
    if (!other) return otherRef;
    return other.kind === "access" ? "przyłącze" : poleDisplayLabel(other);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="space-y-2 border-b border-[var(--color-border)] p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" loading={recomputePending} onClick={onRecompute}>
            <Play size={13} className="mr-1" /> Przelicz obliczenia
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={exportPending}
            disabled={!hasResults}
            onClick={onExport}
          >
            <Download size={13} className="mr-1" /> Eksport XLSX
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={downloadCardsPending}
            disabled={!hasResults}
            onClick={onDownloadCards}
            title="Pobierz karty obliczeń wszystkich policzonych słupów jako pliki PDF w archiwum ZIP"
          >
            <FolderArchive size={13} className="mr-1" /> Karty (ZIP)
          </Button>
        </div>
        {dirty && (
          <p className="font-mono text-[11px] text-amber-600">
            Masz niezapisane zmiany rysunku — zapisz je przed przeliczeniem, inaczej policzy się
            stary rysunek.
          </p>
        )}
        {hasResults && (
          <div className="space-y-0.5 font-mono text-[11px] text-[var(--color-text-secondary)]">
            <p>Obliczono: {fmtDateTime(data?.computed_at)}</p>
            <p>Wersja katalogu norm: {data?.catalog_version_at_compute ?? "—"}</p>
            {data?.stale && (
              <span className="inline-block border border-amber-600 px-1.5 py-0.5 font-bold uppercase text-amber-600">
                Nieaktualne — rysunek zmieniony
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="sm" />
          </div>
        )}
        {!loading && !hasResults && (
          <p className="p-4 font-mono text-xs text-[var(--color-text-muted)]">
            Brak wyników dla tego wariantu — kliknij „Przelicz obliczenia".
          </p>
        )}
        {!loading && hasResults && poles.length === 0 && (
          <p className="p-4 font-mono text-xs text-[var(--color-text-muted)]">
            Rysunek nie zawiera słupów.
          </p>
        )}
        {!loading &&
          hasResults &&
          poles.map((pf) => {
            const ref = pf.properties.ref;
            const label = poleDisplayLabel(pf.properties);
            const r = resultMap?.[ref];
            const isOpen = expanded === ref;
            return (
              <div key={ref} className="border-b border-[var(--color-border-muted)]">
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : ref)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-[var(--color-bg-elevated)]"
                >
                  {isOpen ? (
                    <ChevronDown size={13} className="shrink-0 text-[var(--color-text-muted)]" />
                  ) : (
                    <ChevronRight size={13} className="shrink-0 text-[var(--color-text-muted)]" />
                  )}
                  {/* Etykieta wg priorytetu (historyczny → pełny → ref) + ref w
                      nawiasie, żeby skojarzyć wiersz z kartą/eksportem. */}
                  <span className="w-24 shrink-0 font-mono text-sm font-bold">
                    {label}
                    {label !== ref && (
                      <span className="ml-1 text-[10px] font-normal text-[var(--color-text-tertiary)]">
                        ({ref})
                      </span>
                    )}
                  </span>
                  <span className="w-24 shrink-0 truncate text-[11px] text-[var(--color-text-secondary)]">
                    {pf.properties.params.function || "—"}
                  </span>
                  <span className="flex flex-1 flex-col gap-0.5">
                    {r?.ok ? (
                      <>
                        <ForcePair
                          label="Fx obl/dop:"
                          value={r.target_force_x}
                          allowed={r.max_allowed_target_force_x}
                          passed={r.passed_x}
                        />
                        <ForcePair
                          label="Fy obl/dop:"
                          value={r.target_force_y}
                          allowed={r.max_allowed_target_force_y}
                          passed={r.passed_y}
                        />
                      </>
                    ) : (
                      <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                        {r?.error || "Nie policzono tego słupa."}
                      </span>
                    )}
                  </span>
                  <StatusBadge r={r} />
                </button>
                {isOpen && (
                  <div className="space-y-2 bg-[var(--color-bg-elevated)] px-3 py-2">
                    {r?.error && (
                      <p className="font-mono text-[11px] text-red-600">{r.error}</p>
                    )}
                    {r?.error_type === "NoMultiTypeSplitForTauronConnectionDetails" && (
                      <p className="font-mono text-[10px] text-[var(--color-text-muted)]">
                        Wskazówka: sprawdź liczbę żył w pęczkach przęseł tego słupa — katalog
                        norm zna typowe wiązki (np. 4×AL, 4×AsXSn), pojedyncza żyła zwykle nie
                        ma dopasowania.
                      </p>
                    )}

                    {/* Przęsła słupa wprost ze stanu rysunku — przy błędzie rodziny
                        od razu widać, KTÓRE przęsło i który pęczek zawinił. */}
                    {(() => {
                      const poleSpans = spansOf(drawing, ref);
                      if (!poleSpans.length) return null;
                      return (
                        <div>
                          <p className="section-label mb-0.5">Przęsła tego słupa</p>
                          {poleSpans.map((s, i) => {
                            const sp = s.properties;
                            return (
                              <div
                                key={`${sp.from_ref}-${sp.to_ref}-${i}`}
                                className="flex flex-wrap items-baseline gap-x-2 border-b border-[var(--color-border-muted)] py-0.5 font-mono text-[10px] last:border-0"
                              >
                                <span className="font-bold">
                                  → {otherEndLabel(ref, sp.from_ref, sp.to_ref)}
                                </span>
                                <span
                                  className="text-[var(--color-text-secondary)]"
                                  title={
                                    sp.length_override != null
                                      ? "Długość nadpisana ręcznie (zamiast długości z geometrii)"
                                      : undefined
                                  }
                                >
                                  {fmtMeters(sp.length_override ?? sp.length_m)}
                                  {sp.length_override != null ? " *" : ""}
                                </span>
                                <span>
                                  {sp.bundles.length
                                    ? sp.bundles.map((b, j) => {
                                        const suspect =
                                          !!r?.error && r.error.includes(b.family);
                                        return (
                                          <span key={j}>
                                            {j > 0 && " + "}
                                            <span
                                              className={
                                                suspect
                                                  ? "font-bold text-red-600"
                                                  : "text-[var(--color-text-secondary)]"
                                              }
                                              title={
                                                suspect
                                                  ? "Rodzina z komunikatu błędu"
                                                  : undefined
                                              }
                                            >
                                              {fmtBundle(b)}
                                            </span>
                                          </span>
                                        );
                                      })
                                    : "— brak pęczków —"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {/* Przebieg obliczeń: wzór, wartości jego składników i to samo
                        działanie z podstawionymi liczbami — widać, skąd wynik. */}
                    {([
                      ["Kierunek X", calcSteps(r?.result_x_steps, r?.result_x_description)],
                      ["Kierunek Y", calcSteps(r?.result_y_steps, r?.result_y_description)],
                    ] as const).map(([title, lines]) =>
                      lines.length ? (
                        <div key={title}>
                          <p className="section-label mb-0.5">{title}</p>
                          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-[var(--color-text-secondary)]">
                            {lines.join("\n")}
                          </pre>
                        </div>
                      ) : null,
                    )}
                    <Link
                      to={`/tech-calc/${projectId}/card/${variantId}/${ref}`}
                      className="inline-flex items-center gap-1 font-mono text-[11px] text-[var(--color-primary)] hover:underline"
                    >
                      <FileText size={12} /> Karta obliczeń słupa
                    </Link>
                  </div>
                )}
              </div>
            );
          })}

        {outOfScope.length > 0 && (
          <div className="border-t border-[var(--color-border)]">
            <button
              type="button"
              onClick={() => setOutOfScopeOpen((o) => !o)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-[var(--color-bg-elevated)]"
            >
              {outOfScopeOpen ? (
                <ChevronDown size={13} className="shrink-0 text-[var(--color-text-muted)]" />
              ) : (
                <ChevronRight size={13} className="shrink-0 text-[var(--color-text-muted)]" />
              )}
              <span className="font-mono text-[11px] font-bold uppercase text-[var(--color-text-muted)]">
                Poza opracowaniem ({outOfScope.length})
              </span>
            </button>
            {outOfScopeOpen && (
              <div className="px-3 pb-2">
                <p className="mb-1 text-[10px] text-[var(--color-text-tertiary)]">
                  Te słupy są na rysunku i obciążają sąsiadów swoimi przęsłami, ale same nie są
                  liczone.
                </p>
                {outOfScope.map((pf) => {
                  const label = poleDisplayLabel(pf.properties);
                  return (
                    <div
                      key={pf.properties.ref}
                      className="flex items-baseline gap-2 border-b border-[var(--color-border-muted)] py-1 font-mono text-[11px] last:border-0"
                    >
                      <span className="font-bold">{label}</span>
                      {label !== pf.properties.ref && (
                        <span className="text-[10px] text-[var(--color-text-tertiary)]">
                          ({pf.properties.ref})
                        </span>
                      )}
                      <span className="text-[var(--color-text-secondary)]">
                        {pf.properties.params.function || "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
